import type { Id, ProjectFilters } from '@/shared/types/domain';

/**
 * O ÚNICO lugar onde chaves de query são construídas (tech-stack §6).
 *
 * Nenhuma tela inventa a sua própria convenção: chave divergente é a maior
 * fonte de bug de cache invisível — o tipo de bug que só aparece como "o
 * projeto aprovado não está no calendário" semanas depois.
 *
 * Convenção: do mais genérico para o mais específico, para que
 * `invalidateQueries({ queryKey: ['projects'] })` alcance tudo abaixo dele.
 */

export interface DateRange {
  from: string;
  to: string;
}

export const queryKeys = {
  /** Pessoa + papéis + escopo do usuário logado. */
  me: ['me'] as const,

  /**
   * Os vínculos VIGENTES da pessoa, com nome — o que a seção "Minha área" da
   * sidebar vira (ADR-012 decisão 4).
   *
   * Sob o prefixo `['me']` de propósito: vínculo encerrado muda quem a pessoa é
   * no sistema, e invalidar `queryKeys.me` tem de alcançar a navegação junto.
   * Uma raiz própria deixaria a sidebar apontando para um ministério que a
   * pessoa não lidera mais até o `staleTime` vencer.
   *
   * `profileId` na chave pela mesma razão de `calendarInbox`: num computador
   * compartilhado, a barra de quem saiu não pode sobreviver a quem entra.
   */
  myScopeBindings: (profileId: Id) => ['me', 'scope-bindings', profileId] as const,

  branches: {
    all: ['branches'] as const,
    detail: (id: Id) => ['branches', id] as const,
  },

  coordinations: {
    all: ['coordinations'] as const,
    detail: (id: Id) => ['coordinations', id] as const,
  },

  ministries: {
    all: ['ministries'] as const,
    byCoordination: (id: Id) => ['ministries', 'coordination', id] as const,
    detail: (id: Id) => ['ministries', id] as const,
    /** Onde a pessoa pode ESCREVER (US-08) — não é o mesmo que enxergar. */
    writable: ['ministries', 'writable'] as const,
    /** Quem pode ser responsável pelo projeto (§5.4, projeção reduzida). */
    members: (id: Id) => ['ministries', id, 'members'] as const,
  },

  /**
   * Catálogos do formulário: espaços, categorias, tipos de apoio e origens de
   * recurso. Uma chave só para os quatro — eles mudam quando a secretaria
   * cadastra alguma coisa (raro), sempre são consumidos juntos pela mesma
   * tela, e quatro chaves separadas só multiplicariam o que invalidar depois.
   */
  catalogs: ['catalogs'] as const,

  /**
   * Contagem por situação (US-10, US-11), da view `project_status_counts`.
   * Uma chave só, sem recorte: a view já vem recortada pela RLS e cabe em
   * poucas dezenas de linhas mesmo para o admin — agregar no cliente custa
   * menos que uma chave por escopo, que multiplicaria o que invalidar depois
   * de cada transição (regra 14).
   */
  projectStatusCounts: ['project-status-counts'] as const,

  /**
   * `count_active_leaders()` — quantas pessoas respondem por uma coordenação
   * ou ministério hoje, no alcance de quem pergunta.
   *
   * Sem `profileId` na chave, ao contrário de `myScopeBindings`: o número não
   * é sobre a pessoa, é sobre a estrutura que ela alcança, e o cache inteiro
   * é limpo no logout (`RequireAuth`). Fica sob o prefixo de `ministries` por
   * um motivo prático: quem muda esse número é cadastro de vínculo, o mesmo
   * evento que derruba as outras chaves da família.
   */
  activeLeaderCount: ['ministries', 'active-leader-count'] as const,

  projects: {
    all: ['projects'] as const,
    list: (filters: ProjectFilters) => ['projects', 'list', filters] as const,
    byMinistry: (id: Id) => ['projects', 'ministry', id] as const,
    detail: (id: Id) => ['projects', id] as const,
    /** `project_timeline` (§4.7) — substitui `statusHistory`/`updates`: uma
     *  view só, uma chave só. */
    timeline: (id: Id) => ['projects', id, 'timeline'] as const,
    /** Vizinhança da data (§6.5). Sob o prefixo `['projects']` de propósito,
     *  para que `invalidateAfterTransition` a alcance de graça. */
    neighborhood: (id: Id) => ['projects', id, 'neighborhood'] as const,
    /** Contato do responsável (decisão 30) — só quem decide dispara. */
    contact: (id: Id) => ['projects', id, 'contact'] as const,
    /**
     * A lista de `/rascunhos`: rascunho **e** projeto devolvido para ajuste
     * (decisão 49). Era `drafts`, e o nome mudou junto com o recorte — chave que
     * promete uma população e devolve duas é a origem exata do bug de cache
     * invisível que este arquivo existe para impedir.
     *
     * ⚠️ **A TELA voltou a se chamar "Rascunhos" (21/08/2026), e a chave não
     * acompanha** — de propósito. O rótulo é escolha de produto; a chave
     * descreve o que está guardado nela, e o que está guardado continua sendo
     * duas populações.
     */
    pending: ['projects', 'pending'] as const,
  },

  /**
   * A aba Início. Duas chaves, e a separação é por JANELA, não por bloco:
   * `volume` cobre seis meses atrás a três à frente e serve a três blocos
   * (volume por mês, o que vem por aí, onde está a atividade); `pipeline` cobre
   * noventa dias de decisões e serve só à saúde da esteira.
   *
   * Sob prefixo próprio, e **fora de `['projects']` de propósito**, ainda que
   * as duas leiam `projects_with_context`: aquele prefixo é invalidado inteiro
   * por `invalidateAfterTransition` a cada transição, e recarregar dez meses de
   * histórico porque alguém aprovou um projeto é trabalho que ninguém pediu. O
   * `staleTime` destas duas é longo pela mesma razão — o passado não muda, e o
   * que muda no presente já chega pelos blocos que leem as chaves de projeto.
   *
   * A janela entra na chave porque ela É o recorte: sem isso, a virada do mês
   * devolveria o cache do mês anterior com a coluna "agora" no lugar errado.
   */
  home: {
    all: ['home'] as const,
    volume: (range: DateRange) => ['home', 'volume', range] as const,
    pipeline: (since: string) => ['home', 'pipeline', since] as const,
    /** A densidade das próximas semanas — `agenda_projects`, filial inteira. */
    density: (range: DateRange) => ['home', 'density', range] as const,
  },

  reviewQueue: ['review-queue'] as const,

  /**
   * "Decidido nos últimos 30 dias", abaixo da fila (decisão 28). Chave própria
   * porque o recorte é outro (histórico, não fila) — e o prefixo entra em
   * `invalidateAfterTransition`, senão a decisão que a pessoa acabou de tomar
   * não aparece na lista logo abaixo, que é o pior lugar possível para um
   * cache velho.
   */
  reviewHistory: ['review-history'] as const,

  /** Alerta não bloqueante de sobreposição de espaço (US-20). */
  spaceConflicts: (projectId: Id) => ['space-conflicts', projectId] as const,

  /**
   * O mesmo alerta, mas do lado de quem ESCREVE (decisão 15): ele roda a cada
   * mudança de data ou de espaço, ainda sem projeto salvo, então a chave é o
   * próprio recorte — espaços, janela e o projeto a ignorar.
   */
  spaceConflictPreview: (
    spaceIds: readonly Id[],
    startsAt: string | null,
    endsAt: string | null,
    excludeProjectId?: Id,
  ) =>
    [
      'space-conflict-preview',
      [...spaceIds].sort(),
      startsAt,
      endsAt,
      excludeProjectId ?? null,
    ] as const,

  /** Catálogo de espaços — muda raramente; cabe staleTime longo. */
  spaces: ['spaces'] as const,

  /**
   * O intervalo visível do calendário, e SÓ ele (spec-calendar.md decisão 54).
   *
   * **Filtro não entra na chave** porque não vai ao servidor: o payload de um
   * mês é limitado por construção (RNF-P02 fala em 200 projetos/mês) e filtrar
   * no cliente responde instantaneamente (RNF-P05), mantendo UMA entrada de
   * cache por mês em vez de uma por combinação de filtro — que multiplicaria o
   * que `invalidateAfterTransition` tem de alcançar depois de cada decisão.
   *
   * **FONTE também não entra**: ligar e desligar fonte é recorte de exibição
   * sobre dado já em mãos. Se entrasse, cada toque no seletor disparia uma
   * consulta e a tela piscaria a cada clique.
   */
  calendar: (range: DateRange) => ['calendar', range] as const,

  /**
   * A caixa de entrada do calendário (spec-calendar-ux.md §5.1, decisão 95).
   *
   * **Consulta própria, independente do mês visível** — e a razão é permanente,
   * não uma limitação: o projeto que espera você quase nunca está no mês que
   * você está olhando. Uma pendência de novembro tem de aparecer com a grade em
   * agosto, e nenhuma chave por intervalo faz isso.
   *
   * Fica sob o prefixo `['calendar']` de propósito: `invalidateAfterTransition`
   * já invalida essa raiz inteira, então reenviar um projeto o tira da caixa sem
   * uma linha a mais lá — que é exatamente o cenário da regra 10.
   *
   * `profileId` na chave porque a caixa é POR PESSOA: num computador
   * compartilhado (secretaria), a pendência de quem saiu não pode aparecer para
   * quem entrou.
   */
  calendarInbox: (profileId: Id) => ['calendar', 'inbox', profileId] as const,

  /**
   * A leitura reduzida de UM projeto fora do alcance da RLS (§7.2) — a mesma
   * RPC `agenda_projects`, com o id no lugar do intervalo.
   *
   * Fora do prefixo `['projects']` de propósito: ela não é o projeto, é o que a
   * AGENDA mostra dele. Sob aquele prefixo, `invalidateAfterTransition` a
   * alcançaria de graça — o que é desejável — mas `queryKeys.projects.detail`
   * passaria a ter duas populações com a mesma cara e escopos diferentes.
   */
  agendaProject: (id: Id) => ['calendar', 'project', id] as const,
} as const;
